// Shared model registry. The runner, the agent builder, and any future
// model-pickers all import from here so we have one source of truth for
// "what brains can the user pick?".
//
// Server-side allow-lists in `supabase/functions/_shared/llm.ts`
// (`ANTHROPIC_MODELS`, `OPENROUTER_MODELS`) and per-function allow-lists
// (e.g. `hub-chat`'s `ALLOWED_MODELS`) must stay in sync with the ids
// listed here — if an id isn't whitelisted server-side it will be
// silently downgraded to the default.

export type LlmModel = {
  id: string;
  label: string;
  blurb: string;
  /** Provider family for grouping in pickers. */
  family: "google" | "openai" | "anthropic" | "openrouter";
  /** Whether this is a "premium" tier model (higher credit cost server-side). */
  premium?: boolean;
};

export const AVAILABLE_MODELS: LlmModel[] = [
  // Google — Lovable AI Gateway
  {
    id: "google/gemini-3-flash-preview",
    label: "Gemini 3 Flash",
    blurb: "Fast, balanced — great default",
    family: "google",
  },
  {
    id: "google/gemini-2.5-flash-lite",
    label: "Gemini Flash Lite",
    blurb: "Cheapest, snappy — good for brainstorms",
    family: "google",
  },
  {
    id: "google/gemini-2.5-flash",
    label: "Gemini 2.5 Flash",
    blurb: "Balanced reasoning at low cost",
    family: "google",
  },
  {
    id: "google/gemini-2.5-pro",
    label: "Gemini 2.5 Pro",
    blurb: "Heavy reasoning — best for critique",
    family: "google",
    premium: true,
  },
  // OpenAI — Lovable AI Gateway
  {
    id: "openai/gpt-5-nano",
    label: "GPT-5 Nano",
    blurb: "Tiny + fast — simple tasks",
    family: "openai",
  },
  {
    id: "openai/gpt-5-mini",
    label: "GPT-5 Mini",
    blurb: "Tight rewrites, structured copy",
    family: "openai",
    premium: true,
  },
  {
    id: "openai/gpt-5",
    label: "GPT-5",
    blurb: "Most natural voice + adversarial reasoning",
    family: "openai",
    premium: true,
  },
  // Anthropic — direct
  {
    id: "anthropic/claude-haiku-4-5",
    label: "Claude Haiku 4.5",
    blurb: "Fast Claude — cheap tool-use loops",
    family: "anthropic",
  },
  {
    id: "anthropic/claude-sonnet-4-5",
    label: "Claude Sonnet 4.5",
    blurb: "Best Claude for agentic loops",
    family: "anthropic",
    premium: true,
  },
  {
    id: "anthropic/claude-opus-4",
    label: "Claude Opus 4",
    blurb: "Most thoughtful Claude — slow + deep",
    family: "anthropic",
    premium: true,
  },
  // OpenRouter long-tail
  {
    id: "openrouter/meta-llama/llama-3.3-70b-instruct",
    label: "Llama 3.3 70B",
    blurb: "Open weights, solid all-rounder",
    family: "openrouter",
  },
  {
    id: "openrouter/deepseek/deepseek-chat-v3.1",
    label: "DeepSeek V3.1",
    blurb: "Strong reasoning, cheap",
    family: "openrouter",
  },
  {
    id: "openrouter/deepseek/deepseek-r1",
    label: "DeepSeek R1",
    blurb: "Reasoning model — slow + careful",
    family: "openrouter",
    premium: true,
  },
  {
    id: "openrouter/qwen/qwen-2.5-72b-instruct",
    label: "Qwen 2.5 72B",
    blurb: "Multilingual, structured tasks",
    family: "openrouter",
  },
  {
    id: "openrouter/mistralai/mistral-large-2411",
    label: "Mistral Large",
    blurb: "EU-trained, strong instruction following",
    family: "openrouter",
    premium: true,
  },
  {
    id: "openrouter/x-ai/grok-2-1212",
    label: "Grok 2",
    blurb: "Snarky, current-events leaning",
    family: "openrouter",
  },
];

export function modelLabel(id: string): string {
  return AVAILABLE_MODELS.find((m) => m.id === id)?.label ?? id;
}

export function findModel(id: string): LlmModel | undefined {
  return AVAILABLE_MODELS.find((m) => m.id === id);
}

export const FAMILY_LABELS: Record<LlmModel["family"], string> = {
  google: "Google",
  openai: "OpenAI",
  anthropic: "Anthropic (Claude)",
  openrouter: "OpenRouter",
};